/**
 * /hospitality/fpa — FP&A budget vs actual variance (P7).
 *
 * Budget targets prorated to the current day vs actual MTD revenue.
 * Shows pace status per department (ahead / on_track / watch / behind),
 * variance $ and %, and budgeted vs actual revenue mix.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import { PageHeader, StatCard, Panel, money } from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/fpa")({
  component: FPAPage,
});

// ── Types ──────────────────────────────────────────────────────────────────
interface FPADeptRow {
  department: string;
  budget_monthly: string;
  budget_to_date: string;
  actual_mtd: string;
  variance_amt: string;
  variance_pct: string;
  status: string;
  notes: string | null;
}
interface FPAMixRow {
  department: string;
  actual_mix: string;
  budget_mix: string;
}
interface FPATotals {
  budget_monthly: string;
  budget_to_date: string;
  actual_mtd: string;
  variance_amt: string;
  variance_pct: string;
  status: string;
}
interface FPAResult {
  property: string;
  period: string | null;
  days_elapsed: number | null;
  days_in_month: number | null;
  pace_pct: string | null;
  by_department: FPADeptRow[];
  department_mix: FPAMixRow[];
  total: FPATotals | null;
  note: string | null;
  message?: string;
}

const DEPT_LABELS: Record<string, string> = {
  GAMING: "Gaming",
  ROOMS: "Rooms",
  FNB: "Food & Beverage",
  RETAIL: "Retail",
};

const STATUS_CONFIG: Record<string, { label: string; cls: string }> = {
  ahead:    { label: "Ahead",    cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
  on_track: { label: "On track", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  watch:    { label: "Watch",    cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
  behind:   { label: "Behind",   cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" },
  no_budget:{ label: "No budget",cls: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.no_budget;
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function varianceColor(amt: string): string {
  const n = parseFloat(amt);
  if (n > 0) return "text-emerald-600 dark:text-emerald-400";
  if (n < 0) return "text-red-600 dark:text-red-400";
  return "text-slate-600 dark:text-slate-300";
}

function FPAPage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const q = useQuery({
    queryKey: ["hospitality-fpa"],
    queryFn: () => api<FPAResult>("/api/hospitality/fpa"),
  });

  const data = q.data;
  const total = data?.total;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="FP&A Variance"
        subtitle={
          data?.period
            ? `${data.property} · ${data.period} · Day ${data.days_elapsed} of ${data.days_in_month} (${data.pace_pct} elapsed)`
            : "Budget vs actual — MTD pacing"
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {q.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}

        {!q.isLoading && data?.message && (
          <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
            {data.message}
          </div>
        )}

        {!q.isLoading && data && !data.message && total && (
          <div className="space-y-4">
            {/* Total summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <StatCard label="Monthly budget" value={money(total.budget_monthly, 0)} />
              <StatCard label="Budget to date" value={money(total.budget_to_date, 0)} />
              <StatCard label="Actual MTD" value={money(total.actual_mtd, 0)} />
              <StatCard
                label="Variance"
                value={`${parseFloat(total.variance_amt) >= 0 ? "+" : ""}${money(total.variance_amt, 0)} (${total.variance_pct})`}
              />
            </div>

            {/* By department variance table */}
            <Panel label="Budget vs actual by department">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left py-1.5 pr-3">Department</th>
                    <th className="text-right py-1.5 px-3">Monthly budget</th>
                    <th className="text-right py-1.5 px-3">Budget to date</th>
                    <th className="text-right py-1.5 px-3">Actual MTD</th>
                    <th className="text-right py-1.5 px-3">Variance</th>
                    <th className="text-right py-1.5 px-3">Var %</th>
                    <th className="text-right py-1.5 pl-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_department.map((row) => (
                    <tr key={row.department} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 pr-3">
                        <div className="text-slate-800 dark:text-slate-200 font-medium">
                          {DEPT_LABELS[row.department] ?? row.department}
                        </div>
                        {row.notes && (
                          <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 max-w-[240px] truncate">
                            {row.notes}
                          </div>
                        )}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-500 dark:text-slate-400">
                        {money(row.budget_monthly, 0)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {money(row.budget_to_date, 0)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {money(row.actual_mtd, 0)}
                      </td>
                      <td className={`py-1.5 px-3 text-right tabular-nums text-xs font-medium ${varianceColor(row.variance_amt)}`}>
                        {parseFloat(row.variance_amt) >= 0 ? "+" : ""}{money(row.variance_amt, 0)}
                      </td>
                      <td className={`py-1.5 px-3 text-right tabular-nums text-xs ${varianceColor(row.variance_amt)}`}>
                        {row.variance_pct}
                      </td>
                      <td className="py-1.5 pl-3 text-right">
                        <StatusBadge status={row.status} />
                      </td>
                    </tr>
                  ))}
                  {/* Total row */}
                  <tr className="border-t-2 border-slate-300 dark:border-slate-600 font-semibold">
                    <td className="py-1.5 pr-3 text-xs text-slate-700 dark:text-slate-200">Total</td>
                    <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-500">
                      {money(total.budget_monthly, 0)}
                    </td>
                    <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-700 dark:text-slate-200">
                      {money(total.budget_to_date, 0)}
                    </td>
                    <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-900 dark:text-slate-100">
                      {money(total.actual_mtd, 0)}
                    </td>
                    <td className={`py-1.5 px-3 text-right tabular-nums text-xs ${varianceColor(total.variance_amt)}`}>
                      {parseFloat(total.variance_amt) >= 0 ? "+" : ""}{money(total.variance_amt, 0)}
                    </td>
                    <td className={`py-1.5 px-3 text-right tabular-nums text-xs ${varianceColor(total.variance_amt)}`}>
                      {total.variance_pct}
                    </td>
                    <td className="py-1.5 pl-3 text-right">
                      <StatusBadge status={total.status} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </Panel>

            {/* Revenue mix */}
            {data.department_mix.length > 0 && (
              <Panel label="Revenue mix — actual vs budget">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {data.department_mix.map((m) => (
                    <div key={m.department} className="text-center">
                      <div className="text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
                        {DEPT_LABELS[m.department] ?? m.department}
                      </div>
                      <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
                        {m.actual_mix}
                      </div>
                      <div className="text-[10px] text-slate-400 dark:text-slate-500">
                        Budget: {m.budget_mix}
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            )}

            {data.note && (
              <div className="text-xs text-slate-400 dark:text-slate-500">{data.note}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
